import Link from 'next/link';
import { useTranslation } from 'next-i18next';
import type { BreadcrumbNode } from '@amplience/content-types/typings/h-breadcrumb-node';
import type { AncestorCategory, Category } from '@storefront/lib-global/codegen/types';
import { Breadcrumb, Item } from './ProductListBreadcrumb.styles';

export type BreadcrumbDataType = {
  content: BreadcrumbNode;
};

type ProductListBreadcrumbProps = {
  ancestors: AncestorCategory[];
  category: Category;
  hits?: number;
};

export function ProductListBreadcrumb({ ancestors, category, hits }: ProductListBreadcrumbProps) {
  const { t } = useTranslation(['lib-products-common']);
  const breadcrumbData = [{ key: '', name: t('pdp.breadcrumbs.home'), slug: 'home' }].concat(ancestors, [
    { key: category.key, name: category.name || '', slug: category.slug || '' },
  ]);

  const lastBreadcrumbIndex = breadcrumbData.length - 1;
  const lastBreadcrumb = breadcrumbData.at(-1);

  return (
    <Breadcrumb aria-label={t('breadcrumbs.title')}>
      <p className="visually-hidden" role="status" aria-live="polite">
        {`${lastBreadcrumb?.name} ${hits} ${t('plp.breadcrumbs.results')}`}
      </p>
      <ol>
        {breadcrumbData?.map((item, index) => (
          <Item key={`${item.slug}-${index}`}>
            <Link href={`/${item.key}${index === 0 ? '' : '-c'}`} legacyBehavior passHref>
              {/* It was necessary to create a new <a> tag because NextLink doesn't support the aria-current attribute. */}
              <a {...(index === lastBreadcrumbIndex ? { 'aria-current': 'page' } : {})}>
                {`${item.name} ${index === lastBreadcrumbIndex ? `${hits} ${t('plp.breadcrumbs.results')}` : ''}`}
              </a>
            </Link>
          </Item>
        ))}
      </ol>
    </Breadcrumb>
  );
}
